/**
 * Schedule estimate for an automated multi-stop route (Feedback #53, T9c).
 *
 * Chains sailing legs and per-stop port time (estimateStopPortHours) into
 * arrival/departure times for every stop of one loop, plus the loop total.
 * Pure: no EventBus, no GameState; server-importable. Everything beyond the
 * first arrival is an estimate (price-dependent actions, warehouse detours,
 * engineer refuel/repair and surveys are not predicted).
 *
 * Modes
 *  - 'live': anchored on the ship's real state, NOT route.currentStopIndex
 *    (the client's copy can be stale). The target is derived from
 *    currentRoute.destinationPortId / pendingDeparture.portId by a cyclic
 *    search from currentStopIndex. First arrival comes from estimateShipEta.
 *      sailing           stops[0] = the target stop
 *      held in port      stops[0] = the dock the ship is leaving, if it is a
 *                        route stop (portHoursSource 'live' = the real hold)
 *      docked, no order  stops[0] = that stop, estimated from `now`
 *    A destination that is not a route stop (warehouse detour) becomes a
 *    leading pseudo-stop { stopIndex: -1, flags: ['detour'] } that unloads
 *    everything; the route then resumes at currentStopIndex.
 *  - 'plan' (route editor): starts at stop 0 with an empty hold, simulates two
 *    loops and reports the second (steady state, e.g. after the first buy).
 *
 * loop = { startAt, endAt, hours, isEstimate }: from `now` (live) or the first
 * stop's arrival (plan) to the arrival back at the first real stop.
 * Opportunistic routes choose stops dynamically: only the current leg is
 * returned (portHoursSource 'none') and loop is null.
 */
import { estimateShipEta } from './eta.js';
import { estimateStopPortHours } from './routeStopEstimate.js';
import { calculateRoute } from './seaRoutes.js';

const HOUR_MS = 3600000;
const DETOUR_ACTIONS = [{ type: 'warehouse_unload', cargoType: '__all__', quantity: 'all' }];

// ---------- leg distance memo ----------
// Only real waypoint paths are cached: a 2-point result is the fallback used
// while sea-route data is still loading (or no path exists) and must not stick.
const legCache = new Map();

/** Drop memoised leg distances (call after sea-route data is (re)loaded or edited). */
export function clearLegDistanceCache() { legCache.clear(); }

function legDistance(from, to) {
  if (!from?.position || !to?.position) return 0;
  const key = `${from.id}|${to.id}`;
  const hit = legCache.get(key);
  if (hit !== undefined) return hit;
  const r = calculateRoute(from.position, to.position);
  if (r.waypoints && r.waypoints.length > 2) legCache.set(key, r.distance);
  return r.distance;
}

// ---------- context lookups (all optional, tolerant of shape) ----------
function lookup(source, key) {
  if (!source) return undefined;
  if (typeof source === 'function') return source(key);
  if (source instanceof Map) return source.get(key);
  return source[key];
}

/** null = no warehouse here, undefined = unknown, else { cargo, freeCapacity }. */
function warehouseAt(warehouses, portId) {
  if (!warehouses) return undefined;
  const wh = lookup(warehouses, 'wh_' + portId) ?? lookup(warehouses, portId);
  if (!wh) return null;
  const freeCapacity = typeof wh.getAvailableCapacity === 'function'
    ? wh.getAvailableCapacity() : (wh.freeCapacity ?? Infinity);
  return { cargo: wh.cargo || [], freeCapacity };
}

function simLots(ship) {
  return (ship.cargo || []).map(c => ({
    type: c.type, quantity: c.quantity || 0,
    contractId: c.contractId || null, destinationPort: c.destinationPort || null
  }));
}

function blockedResult(blockedReason) { return { blockedReason, stops: [], loop: null }; }

function legSpeed(ship) {
  return ship.status === 'drydock' ? ship.getCurrentSpeed(100) : ship.getCurrentSpeed();
}

/**
 * @param {TradeRoute} route  { stops, mode, enabled, currentStopIndex, interestList }
 * @param {Ship} ship
 * @param {object} opts
 * @param {Date}   opts.now
 * @param {Function|Map|object} opts.getPort  portId -> Port (function, Map or object)
 * @param {object} [opts.manager]       { canPerformAction }; live mode requires it
 * @param {Map|Function|object} [opts.warehouses] 'wh_<portId>' (or portId) -> Warehouse;
 *                                      absent = unknown, present but missing = none at that port
 * @param {Function|Map|object} [opts.portStock] portId -> { cargoType: TEU }
 * @param {'live'|'plan'} [opts.mode='live']
 * @returns {{ blockedReason:string|null, stops:Array, loop:object|null }}
 */
export function estimateRouteSchedule(route, ship, opts = {}) {
  const { now, manager, warehouses, portStock } = opts;
  const mode = opts.mode === 'plan' ? 'plan' : 'live';
  const getPort = id => lookup(opts.getPort, id);

  if (!ship || !route || !(now instanceof Date)) return blockedResult('no-ship');
  const stops = route.stops || [];
  const n = stops.length;
  if (n < 2) return blockedResult('too-few-stops');
  if (mode === 'live' ? !manager : manager === null) return blockedResult('no-manager');
  if (mode === 'live' && !route.enabled) return blockedResult('route-paused');
  const speed = legSpeed(ship);
  if (!(speed > 0)) return blockedResult('no-speed');

  const opportunistic = route.mode === 'opportunistic';
  const hoursBetween = (a, b) => (b.getTime() - a.getTime()) / HOUR_MS;
  const addHours = (d, h) => new Date(d.getTime() + h * HOUR_MS);

  const fuelPerLeg = dist => ship.estimateFuelForVoyage ? ship.estimateFuelForVoyage(dist) : 0;

  // ---------- anchor ----------
  const out = [];
  const sim = { lots: [], fuel: ship.fuel || 0 };
  let cursor;           // index (mod n) of the next stop to simulate
  let clock;            // time the ship is ready to leave the last emitted point
  let prevPort = null;  // port the ship departs from for the next leg
  let remaining = n;    // real stops still to emit this loop
  let skipFirstLeg = false;
  const loopStart = now;

  const emit = (stopIndex, port, arriveAt, travelHours, portHours, source, flags, departAt, fuelShort) => {
    out.push({
      stopIndex, portId: port?.id ?? null, arriveAt, departAt, travelHours, portHours,
      portHoursSource: source, flags: flags || [], fuelShort: !!fuelShort
    });
  };

  /** Simulate port work at a stop and emit it. */
  const doStop = (idx, port, arriveAt, travelHours, fuelShort, extra = {}) => {
    const stop = stops[idx];
    const r = estimateStopPortHours(ship, port, stop.actions || [], sim, {
      manager,
      portStock: lookup(portStock, stop.portId),
      warehouse: warehouseAt(warehouses, stop.portId),
      interestList: route.interestList,
      ...extra
    });
    emit(idx, port, arriveAt, travelHours, r.hours, r.hours > 0 ? 'estimate' : 'none',
      r.flags, addHours(arriveAt, r.hours), fuelShort);
    return r;
  };

  /** Sail a leg from prevPort to `port` leaving at `clock`; returns { arriveAt, travelHours, fuelShort }. */
  const sail = port => {
    const dist = legDistance(prevPort, port);
    const travelHours = dist > 0 ? dist / speed : 0;
    const need = fuelPerLeg(dist);
    const fuelShort = need > sim.fuel + 1e-9;
    sim.fuel = Math.max(0, sim.fuel - need);
    return { arriveAt: addHours(clock, travelHours), travelHours, fuelShort };
  };

  if (mode === 'plan') {
    cursor = 0;
    clock = now;
    const first = getPort(stops[0].portId);
    if (!first) return blockedResult('no-ship');
    // Stop 0 is reached with no leg; handled by the shared loop below.
    prevPort = first;
    skipFirstLeg = true;
  } else {
    const anchored = anchorLive();
    if (anchored.blocked) return blockedResult(anchored.blocked);
    if (opportunistic) return anchored.result;
  }

  // ---------- live anchoring ----------
  function anchorLive() {
    const sailing = ship.status === 'sailing' && ship.currentRoute;
    const destId = sailing ? ship.currentRoute.destinationPortId : ship.pendingDeparture?.portId;
    const base = route.currentStopIndex || 0;
    const findFrom = portId => {
      for (let k = 0; k < n; k++) if (stops[(base + k) % n].portId === portId) return (base + k) % n;
      return -1;
    };

    sim.lots = simLots(ship);

    if (destId) {
      const eta = estimateShipEta(ship, { now });
      if (eta.blockedReason) return { blocked: eta.blockedReason };
      const destIdx = findFrom(destId);
      const dest = getPort(destId);
      if (!dest) return { blocked: 'no-route-data' };

      if (opportunistic) {
        emit(destIdx, dest, eta.etaDate, eta.travelHours, 0, 'none', [], null, false);
        return { result: { blockedReason: null, stops: out, loop: null } };
      }

      // Held in port: the dock being left leads the list with its real hold.
      let startsAtDock = false;
      if (!sailing) {
        const dockIdx = ship.currentPortId ? findFrom(ship.currentPortId) : -1;
        const dockPort = dockIdx >= 0 ? getPort(ship.currentPortId) : null;
        if (dockPort && dockIdx !== destIdx) {
          const holdHours = eta.holdHours || 0;
          emit(dockIdx, dockPort, now, 0, holdHours, holdHours > 0 ? 'live' : 'none', [], eta.departAt, false);
          startsAtDock = true;
        }
      }

      if (destIdx < 0) {
        // Warehouse detour: unload everything, then resume at the route's own next stop.
        const r = estimateStopPortHours(ship, dest, DETOUR_ACTIONS, sim,
          { manager, warehouse: warehouseAt(warehouses, destId) });
        const fuelAtArrival = Math.max(0, sim.fuel - fuelPerLeg(eta.travelHours * speed));
        sim.fuel = fuelAtArrival;
        emit(-1, dest, eta.etaDate, eta.travelHours, r.hours, r.hours > 0 ? 'estimate' : 'none',
          [...new Set([...r.flags, 'detour'])], addHours(eta.etaDate, r.hours), false);
        cursor = base % n;
        remaining = startsAtDock ? n - 1 : n;
      } else {
        // First arrival at the target stop is exact (from estimateShipEta).
        const fuelShort = fuelPerLeg(eta.travelHours * speed) > sim.fuel + 1e-9;
        sim.fuel = Math.max(0, sim.fuel - fuelPerLeg(eta.travelHours * speed));
        doStop(destIdx, dest, eta.etaDate, eta.travelHours, fuelShort);
        remaining = startsAtDock ? n - 2 : n - 1;
        cursor = (destIdx + 1) % n;
      }
      prevPort = dest;
      clock = out[out.length - 1].departAt;
      return {};
    }

    // Docked with nothing ordered: estimate the stop the ship is at, from now.
    const dockIdx = ship.currentPortId ? findFrom(ship.currentPortId) : -1;
    const dockPort = dockIdx >= 0 ? getPort(ship.currentPortId) : null;
    if (!dockPort) return { blocked: 'no-destination' };
    const release = ship.getPortReleaseDate ? ship.getPortReleaseDate(now) : now;
    if (!release) return { blocked: 'awaiting-orders' };
    if (opportunistic) return { blocked: 'no-destination' };
    const start = release;
    const r = doStop(dockIdx, dockPort, now, 0, false);
    // Ship cannot leave before its existing hold ends either.
    const last = out[out.length - 1];
    if (last.departAt < start) {
      last.portHours = hoursBetween(now, start);
      last.departAt = start;
      if (r.hours <= 0 && last.portHours > 0) last.portHoursSource = 'live';
    }
    remaining = n - 1;
    cursor = (dockIdx + 1) % n;
    prevPort = dockPort;
    clock = last.departAt;
    return {};
  }

  // ---------- chain remaining stops ----------
  const firstRealPort = out.find(s => s.stopIndex >= 0)?.portId ?? stops[cursor % n].portId;

  /** Simulate and emit `count` consecutive stops from `cursor`. false when a port is unknown. */
  const runLoop = count => {
    for (let i = 0; i < count; i++) {
      const idx = cursor % n;
      const port = getPort(stops[idx].portId);
      if (!port) return false;
      let leg = { arriveAt: clock, travelHours: 0, fuelShort: false };
      if (skipFirstLeg) skipFirstLeg = false;
      else leg = sail(port);
      doStop(idx, port, leg.arriveAt, leg.travelHours, leg.fuelShort);
      prevPort = port;
      clock = out[out.length - 1].departAt;
      cursor = (idx + 1) % n;
    }
    return true;
  };

  if (mode === 'plan') {
    // Loop 1 warms up the simulated hold/fuel and positions the clock; report loop 2.
    if (!runLoop(n)) return blockedResult('no-route-data');
    out.length = 0;
    if (!runLoop(n)) return blockedResult('no-route-data');
    const back = sail(getPort(stops[0].portId));
    return finish(out[0].arriveAt, back.arriveAt);
  }

  if (remaining > 0 && !runLoop(remaining)) return blockedResult('no-route-data');
  const home = getPort(firstRealPort);
  if (!home) return blockedResult('no-route-data');
  const back = sail(home);
  return finish(loopStart, back.arriveAt);

  function finish(startAt, endAt) {
    return {
      blockedReason: null,
      stops: out,
      loop: { startAt, endAt, hours: hoursBetween(startAt, endAt), isEstimate: true }
    };
  }
}
